/**
 * MediaPipe Pose Landmarker — auto-deteção client-side.
 * - Modelo HEAVY (FP32) para máxima precisão em fotografias estáticas.
 * - Lazy-load + cache da instância (~30MB inicial, depois 0).
 * - Mapeia 33 keypoints MediaPipe → 11 (frente) / 10 (lado) / 20 (costas).
 * - Devolve confiança média baseada em visibility por landmark.
 *
 * Convenção de coordenadas:
 * - MediaPipe usa "left/right" do ponto de vista do SUJEITO.
 * - O nosso UI assume convenção do OBSERVADOR (image-left = key "_l").
 * - Em vista FRONTAL: image-left = sujeito-direito → mapeamos cruzado.
 * - Em vista POSTERIOR: image-left = sujeito-esquerdo → mapeamento direto.
 */
import { FilesetResolver, PoseLandmarker } from "@mediapipe/tasks-vision";

const MODEL_URL = "https://storage.googleapis.com/mediapipe-models/pose_landmarker/pose_landmarker_heavy/float16/1/pose_landmarker_heavy.task";
const WASM_URL  = "https://cdn.jsdelivr.net/npm/@mediapipe/tasks-vision@0.10.35/wasm";

// MediaPipe pose landmark indices
const MP = {
  nose: 0,
  left_eye: 2, right_eye: 5,
  left_ear: 7, right_ear: 8,
  left_shoulder: 11, right_shoulder: 12,
  left_elbow: 13, right_elbow: 14,
  left_wrist: 15, right_wrist: 16,
  left_hip: 23, right_hip: 24,
  left_knee: 25, right_knee: 26,
  left_ankle: 27, right_ankle: 28,
  left_heel: 29, right_heel: 30,
  left_foot_index: 31, right_foot_index: 32,
};

let _landmarker = null;
let _loading = null;

async function getLandmarker() {
  if (_landmarker) return _landmarker;
  if (_loading) return _loading;
  _loading = (async () => {
    const fileset = await FilesetResolver.forVisionTasks(WASM_URL);
    _landmarker = await PoseLandmarker.createFromOptions(fileset, {
      baseOptions: { modelAssetPath: MODEL_URL, delegate: "GPU" },
      runningMode: "IMAGE",
      numPoses: 1,
      minPoseDetectionConfidence: 0.5,
      minPosePresenceConfidence: 0.5,
      minTrackingConfidence: 0.5,
      outputSegmentationMasks: false,
    });
    _loading = null;
    return _landmarker;
  })();
  return _loading;
}

function loadHTMLImage(dataURL) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = (e) => reject(e);
    img.crossOrigin = "anonymous";
    img.src = dataURL;
  });
}

const MIN_VIS = 0.5;
function pick(lm, idx) {
  const p = lm[idx];
  if (!p) return null;
  if ((p.visibility ?? 0) < MIN_VIS) return null;
  return { x: clamp01(p.x), y: clamp01(p.y), v: p.visibility ?? 0 };
}
function clamp01(v) { return Math.max(0, Math.min(1, v)); }
function avg(...pts) {
  const valid = pts.filter(Boolean);
  if (!valid.length) return null;
  const x = valid.reduce((s, p) => s + p.x, 0) / valid.length;
  const y = valid.reduce((s, p) => s + p.y, 0) / valid.length;
  const v = valid.reduce((s, p) => s + (p.v ?? 0), 0) / valid.length;
  return { x, y, v };
}
function offset(p, dx, dy) {
  return p ? { x: clamp01(p.x + dx), y: clamp01(p.y + dy), v: p.v ?? 0 } : null;
}
const xy = (p) => (p ? { x: p.x, y: p.y } : undefined);

// --- View mappers --------------------------------------------------------
function mapFront(lm) {
  // image-left == subject's right (espelho)
  const out = {};
  const set = (k, p) => { if (p) out[k] = { x: p.x, y: p.y }; };

  set("head", pick(lm, MP.nose));
  set("shoulder_l", pick(lm, MP.right_shoulder));
  set("shoulder_r", pick(lm, MP.left_shoulder));
  set("hip_l",      pick(lm, MP.right_hip));
  set("hip_r",      pick(lm, MP.left_hip));
  set("knee_l",     pick(lm, MP.right_knee));
  set("knee_r",     pick(lm, MP.left_knee));
  set("ankle_l",    pick(lm, MP.right_ankle));
  set("ankle_r",    pick(lm, MP.left_ankle));
  set("foot_l",     pick(lm, MP.right_foot_index));
  set("foot_r",     pick(lm, MP.left_foot_index));
  return out;
}

function mapSide(lm) {
  // Em vista lateral só um lado fica visível; escolhe o lado com maior visibility.
  const leftVis = ((lm[MP.left_shoulder]?.visibility ?? 0) + (lm[MP.left_hip]?.visibility ?? 0)) / 2;
  const rightVis = ((lm[MP.right_shoulder]?.visibility ?? 0) + (lm[MP.right_hip]?.visibility ?? 0)) / 2;
  const useLeft = leftVis >= rightVis;
  const idx = useLeft
    ? { ear: MP.left_ear, shoulder: MP.left_shoulder, hip: MP.left_hip, knee: MP.left_knee, ankle: MP.left_ankle, foot: MP.left_foot_index }
    : { ear: MP.right_ear, shoulder: MP.right_shoulder, hip: MP.right_hip, knee: MP.right_knee, ankle: MP.right_ankle, foot: MP.right_foot_index };

  const ear      = pick(lm, idx.ear);
  const nose     = pick(lm, MP.nose);
  const shoulder = pick(lm, idx.shoulder);
  const hip      = pick(lm, idx.hip);
  const knee     = pick(lm, idx.knee);
  const ankle    = pick(lm, idx.ankle);
  const foot     = pick(lm, idx.foot);   // dedos — referência auxiliar de orientação

  // --- Ear refinement low-cost (Fase 2) -----------------------------------
  // Em vista lateral, a orelha (tragus/lóbulo) NUNCA é anterior ao nariz.
  // O MediaPipe pode colocar o ponto da orelha ligeiramente à frente do
  // perfil quando a pose detection se confunde (ângulos não puramente 90°).
  // Se a orelha sair mais anterior que o nariz, fazemos clamp para uma
  // pequena margem posterior ao nariz (-1.5% da largura). Sem face mesh.
  let earClamped = ear;
  if (ear && nose && shoulder) {
    const facingDir = Math.sign((nose.x - shoulder.x) || 0);
    if (facingDir !== 0) {
      const noseSide = facingDir;          // +1 face direita, -1 face esquerda
      const margin = 0.015;                // 1.5% da largura — pequena margem posterior
      // Calcula a "linha do nariz" e quanto a orelha excede no sentido anterior.
      const overshoot = (ear.x - nose.x) * noseSide; // > 0 se a orelha é anterior ao nariz
      if (overshoot > -margin) {
        // Orelha demasiado anterior → clamp para nose - margin × noseSide
        earClamped = { ...ear, x: nose.x - noseSide * margin };
      }
    }
  }

  const out = {};
  if (earClamped) out.ear      = xy(earClamped);
  if (shoulder)   out.shoulder = xy(shoulder);
  if (hip)        out.hip      = xy(hip);
  if (knee)       out.knee     = xy(knee);
  if (ankle)      out.ankle    = xy(ankle);
  if (foot)       out.foot     = xy(foot);

  // Pontos da coluna não existem nativamente no MediaPipe.
  // Interpolamos proporcionalmente ao longo do eixo real ombro→anca do sujeito.
  // Aplicamos também um pequeno deslocamento ANTERIOR (~ comprimento do tronco)
  // para que os corpos vertebrais caiam dentro do tronco e não sobre o eixo
  // articular ombro/anca, evitando o efeito "coluna atrás do corpo".
  // Direção anterior é detetada pela posição da orelha relativa ao ombro
  // (em vista lateral a orelha fica à frente do ombro). Sem orelha → sem offset.
  if (shoulder && hip) {
    const facingDir = ear ? Math.sign((ear.x - shoulder.x) || 0) : 0; // -1 ou +1
    const trunkLen = Math.hypot(hip.x - shoulder.x, hip.y - shoulder.y) || 0.001;
    const baseAx = facingDir * trunkLen * 0.06; // ~6% do tronco para a frente

    // anteriorScale: C7/sacro encostados às articulações; T6/L3 puxados mais
    // para a frente para refletirem o corpo vertebral central.
    const lerp = (t, anteriorScale = 1) => ({
      x: clamp01(shoulder.x + (hip.x - shoulder.x) * t + baseAx * anteriorScale),
      y: clamp01(shoulder.y + (hip.y - shoulder.y) * t),
      v: ((shoulder.v ?? 0) + (hip.v ?? 0)) / 2,
    });

    const cervical = lerp(0.02, 0.5);   // C7 — ligeiramente posterior ao ombro mas sobre o tronco
    const thoracic = lerp(0.33, 1.0);   // T6 — centro torácico
    const lumbar   = lerp(0.70, 1.0);   // L3 — região lombar média
    const sacrum   = lerp(0.98, 0.5);   // sacro — próximo da PSIS/bacia posterior
    out.cervical = xy(cervical);
    out.thoracic = xy(thoracic);
    out.lumbar   = xy(lumbar);
    out.sacrum   = xy(sacrum);
  }
  return out;
}

function mapBack(lm) {
  // Vista posterior: image-left = subject-left → mapeamento direto.
  const sl = pick(lm, MP.left_shoulder);
  const sr = pick(lm, MP.right_shoulder);
  const hl = pick(lm, MP.left_hip);
  const hr = pick(lm, MP.right_hip);
  const el = pick(lm, MP.left_ear);
  const er = pick(lm, MP.right_ear);
  const nose = pick(lm, MP.nose);

  const out = {};
  const set = (k, p) => { if (p) out[k] = xy(p); };
  set("shoulder_l", sl);
  set("shoulder_r", sr);
  set("hip_l", hl);
  set("hip_r", hr);
  set("ear_l", el);
  set("ear_r", er);
  // Membros inferiores (deteção direta; abaixo de MIN_VIS ficam por colocar manualmente, sem estimativas).
  set("knee_l", pick(lm, MP.left_knee));
  set("knee_r", pick(lm, MP.right_knee));
  set("ankle_l", pick(lm, MP.left_ankle));
  set("ankle_r", pick(lm, MP.right_ankle));
  set("heel_l", pick(lm, MP.left_heel));
  set("heel_r", pick(lm, MP.right_heel));

  // head_top — ligeiramente acima do nariz (em vista posterior, nariz não é detetado de forma fiável,
  //  mas o MP geralmente devolve uma estimativa).
  if (nose) set("head_top", offset(nose, 0, -0.04));

  // occipital — entre os 2 ouvidos
  const occipital = avg(el, er);
  if (occipital) set("occipital", offset(occipital, 0, 0.02));

  // c7 — entre os 2 ombros (ligeiramente acima)
  const c7 = sl && sr ? offset(avg(sl, sr), 0, -0.01) : null;
  if (c7) set("c7", c7);

  // thoracic — meio do tronco (mid-shoulders ↔ mid-hips, mais perto de cima)
  if (sl && sr && hl && hr) {
    const sMid = avg(sl, sr);
    const hMid = avg(hl, hr);
    const thoracic = sMid && hMid
      ? { x: (sMid.x * 0.65 + hMid.x * 0.35), y: (sMid.y * 0.65 + hMid.y * 0.35), v: ((sMid.v + hMid.v) / 2) }
      : null;
    if (thoracic) set("thoracic", xy(thoracic));
    const lumbar = sMid && hMid
      ? { x: (sMid.x * 0.30 + hMid.x * 0.70), y: (sMid.y * 0.30 + hMid.y * 0.70), v: ((sMid.v + hMid.v) / 2) }
      : null;
    if (lumbar) set("lumbar", xy(lumbar));
    if (hMid) set("sacrum", xy(offset(hMid, 0, 0.02)));
    // Omoplatas — aprox. 1/3 entre ombro e linha média do tronco, abaixo do ombro
    const scl = sl && sMid ? { x: (sl.x * 0.75 + sMid.x * 0.25), y: sl.y + 0.05, v: sl.v } : null;
    const scr = sr && sMid ? { x: (sr.x * 0.75 + sMid.x * 0.25), y: sr.y + 0.05, v: sr.v } : null;
    if (scl) set("scapula_l", xy(scl));
    if (scr) set("scapula_r", xy(scr));
  }
  return out;
}

// --- Silhouette refinement (vista lateral) -------------------------------
/**
 * Refina os pontos da coluna lateral (cervical/thoracic/lumbar/sacrum) com
 * PRECISÃO ANATÓMICA visual.
 *
 * Princípios:
 *  1. Baseline ombro→anca define o eixo axial. A coluna real fica próxima
 *     deste eixo na maioria dos pacientes — só se afasta visivelmente em
 *     cifoses/lordoses marcadas.
 *  2. Para cada y de interesse, medimos a profundidade real do tronco
 *     (anterior↔posterior) e a posição da pele posterior, com mediana de
 *     bg (robusta a vinhetas/sombras).
 *  3. Posicionamos a coluna a 25% da profundidade do tronco a anterior
 *     da pele posterior — o que coloca o ponto perto do corpo vertebral
 *     anatómico, e não na pele/spinous process.
 *  4. CLAMP ANATÓMICO: a posição final não pode afastar-se mais de ~3.5%
 *     da largura da imagem do baseline ombro→anca. Isto impede o "S"
 *     exagerado em pacientes com roupa larga ou sombras.
 *  5. Suavização leve cruzada nos 4 pontos para evitar kinks.
 *
 *  Sem snapping isolado por ponto. Sem dependências externas. Sem modelos
 *  pesados. Sub-50ms num portrait típico.
 */
function refineSpineToContour(img, mapped, patientHints = null) {
  const ear = mapped.ear, shoulder = mapped.shoulder, hip = mapped.hip;
  if (!ear || !shoulder || !hip) {
    return { points: null, confidence: 0 };
  }
  const facingDir = Math.sign((ear.x - shoulder.x) || 0);
  if (facingDir === 0) return { points: null, confidence: 0 };
  const backDir = -facingDir;

  // --- Body Depth Factor (Fase 1) ----------------------------------------
  // Adapta ANTERIOR_FRAC e MAX_DEV ao tipo corporal. Em sujeitos magros
  // (BMI baixo) o tronco real é fino → corpo vertebral fica mais anterior
  // (fração maior da profundidade) e clamp mais apertado. Em sujeitos com
  // BMI alto, o tronco é mais profundo → fração menor (fica mais posterior
  // dentro do tronco) e clamp ligeiramente mais largo.
  // Curva sigmoid suave centrada em BMI=24 para evitar saltos abruptos.
  let ANTERIOR_FRAC = 0.25;
  let MAX_DEV = 0.035;
  let bodyDepthFactor = 1.0; // 0.85 (magro) — 1.15 (BMI alto)
  if (patientHints && Number.isFinite(patientHints.bmi) && patientHints.bmi > 0) {
    const bmi = Math.max(14, Math.min(45, patientHints.bmi));
    // Mapeamento suave: BMI 18 → 0.88 · BMI 24 → 1.0 · BMI 32 → 1.12
    bodyDepthFactor = 1 + Math.tanh((bmi - 24) / 7) * 0.15;
    // Clamp anatómico — adapta-se mas nunca sai do envelope seguro
    ANTERIOR_FRAC = 0.28 / bodyDepthFactor;     // 0.32 (magro) → 0.25 (atlético) → 0.22 (BMI alto)
    MAX_DEV = Math.max(0.028, Math.min(0.045, 0.035 * bodyDepthFactor));
  } else if (patientHints && patientHints.gender) {
    // Sem BMI mas com género — ligeira adaptação heurística
    if (patientHints.gender === "feminino") MAX_DEV = 0.032;
  }
  // ------------------------------------------------------------------------

  const W = 240;
  const aspect = (img.naturalHeight || img.height) / (img.naturalWidth || img.width || 1);
  const H = Math.max(80, Math.round(W * aspect));
  const c = document.createElement("canvas");
  c.width = W; c.height = H;
  const ctx = c.getContext("2d", { willReadFrequently: true });
  try { ctx.drawImage(img, 0, 0, W, H); } catch { return { points: null, confidence: 0 }; }
  const data = ctx.getImageData(0, 0, W, H).data;

  const lum = (x, y) => {
    const i = (y * W + x) * 4;
    return 0.299 * data[i] + 0.587 * data[i + 1] + 0.114 * data[i + 2];
  };

  // bg robusto = mediana dos 4 cantos (margem 2px)
  const corners = [lum(2, 2), lum(W - 3, 2), lum(2, H - 3), lum(W - 3, H - 3)].sort((a, b) => a - b);
  const bg = (corners[1] + corners[2]) / 2;
  const THR = 22;

  // Para cada y normalizado, devolve { backX, frontX, depth } em px (ou null).
  // backX = posição da pele posterior; frontX = pele anterior. Sem suposições
  // sobre seed — faz scan dos limites para o centro de ambos os lados.
  const bodyExtents = (yNorm) => {
    const y = Math.max(2, Math.min(H - 3, Math.round(yNorm * (H - 1))));
    let backX = -1, frontX = -1;
    // back side: scan a partir da margem no lado "back"
    const bStart = backDir > 0 ? W - 2 : 1;
    const bEnd   = backDir > 0 ? 1 : W - 2;
    const bDir   = backDir > 0 ? -1 : 1;
    for (let x = bStart; (bDir > 0 ? x <= bEnd : x >= bEnd); x += bDir) {
      if (Math.abs(lum(x, y) - bg) >= THR) { backX = x; break; }
    }
    // front side: scan a partir da margem no lado "facing"
    const fStart = facingDir > 0 ? W - 2 : 1;
    const fEnd   = facingDir > 0 ? 1 : W - 2;
    const fDir   = facingDir > 0 ? -1 : 1;
    for (let x = fStart; (fDir > 0 ? x <= fEnd : x >= fEnd); x += fDir) {
      if (Math.abs(lum(x, y) - bg) >= THR) { frontX = x; break; }
    }
    if (backX < 0 || frontX < 0) return null;
    return { backX, frontX, depth: Math.abs(frontX - backX) };
  };

  // Baseline ombro→anca em coords normalizadas [0,1]
  const baselineAt = (yNorm) => {
    const dy = Math.max(1e-6, hip.y - shoulder.y);
    const t = (yNorm - shoulder.y) / dy;
    return shoulder.x + (hip.x - shoulder.x) * t;
  };

  // Constantes anatómicas (ANTERIOR_FRAC e MAX_DEV) já definidas acima
  // pelo Body Depth Factor (Fase 1).

  const keys = ["cervical", "thoracic", "lumbar", "sacrum"];
  const out = {};
  let successN = 0;

  for (const key of keys) {
    const base = mapped[key];
    if (!base) continue;
    const ext = bodyExtents(base.y);
    const baselineX = baselineAt(base.y);
    if (!ext) {
      // Sem contorno → manter base (lerp), sem inventar curvatura.
      out[key] = { x: baselineX, y: base.y };
      continue;
    }
    const skinBackX = ext.backX / (W - 1);
    const torsoDepth = ext.depth / (W - 1);
    // Posição candidata: 25% da profundidade do tronco a anterior da pele
    // posterior (sentido facingDir = anterior).
    let fx = skinBackX + facingDir * torsoDepth * ANTERIOR_FRAC;
    // Anatomical clamp — afasta no máximo MAX_DEV do baseline
    const dev = fx - baselineX;
    if (Math.abs(dev) > MAX_DEV) {
      fx = baselineX + Math.sign(dev) * MAX_DEV;
    }
    out[key] = { x: Math.max(0, Math.min(1, fx)), y: base.y };
    successN += 1;
  }

  // Suavização leve cruzada (evita kinks entre os 4 pontos):
  //   newX = 0.5·current + 0.25·prev + 0.25·next  (apenas pontos interiores)
  const xs = keys.map(k => out[k]?.x ?? null);
  for (let i = 1; i < keys.length - 1; i++) {
    if (xs[i] != null && xs[i - 1] != null && xs[i + 1] != null && out[keys[i]]) {
      out[keys[i]].x = 0.5 * xs[i] + 0.25 * xs[i - 1] + 0.25 * xs[i + 1];
    }
  }

  // Confiança = fração de pontos que tiveram contorno válido.
  // Threshold no consumer (silhouetteWarn < 0.75) lida com casos degenerados.
  const confidence = successN / keys.length;
  return { points: out, confidence };
}

// --- Public API ----------------------------------------------------------
export async function detectPose(dataURL, view = "front", patientHints = null) {
  if (!dataURL) return { landmarks: {}, confidence: 0, detected: false, error: "no-image" };
  try {
    const [landmarker, img] = await Promise.all([getLandmarker(), loadHTMLImage(dataURL)]);
    const result = landmarker.detect(img);
    const lm = result?.landmarks?.[0];
    if (!lm || !lm.length) return { landmarks: {}, confidence: 0, detected: false, error: "no-pose" };

    const mapped = view === "side" ? mapSide(lm) : view === "back" ? mapBack(lm) : mapFront(lm);

    // Refinamento da silhueta lateral — projeta C7/T6/L3/Sacro sobre o
    // contorno posterior real do corpo. Apenas em vista lateral; sem
    // dependências externas. Adapta-se ao tipo corporal via patientHints.
    let silhouetteConfidence = null;
    if (view === "side") {
      try {
        const refined = refineSpineToContour(img, mapped, patientHints);
        if (refined.points && refined.confidence > 0) {
          for (const k of ["cervical", "thoracic", "lumbar", "sacrum"]) {
            if (refined.points[k]) mapped[k] = refined.points[k];
          }
          silhouetteConfidence = refined.confidence;
        }
      } catch (e) { console.warn("silhouette refine failed", e); }
    }

    // Confiança = média de visibility dos pontos principais (sem os interpolados)
    const coreIdx = view === "side"
      ? [MP.left_shoulder, MP.right_shoulder, MP.left_hip, MP.right_hip, MP.left_ankle, MP.right_ankle]
      : view === "back"
      ? [MP.left_shoulder, MP.right_shoulder, MP.left_hip, MP.right_hip, MP.left_ear, MP.right_ear]
      : [MP.nose, MP.left_shoulder, MP.right_shoulder, MP.left_hip, MP.right_hip,
         MP.left_knee, MP.right_knee, MP.left_ankle, MP.right_ankle];
    const visList = coreIdx.map(i => lm[i]?.visibility ?? 0).filter(v => v > 0);
    const avgVis = visList.length ? visList.reduce((s, v) => s + v, 0) / visList.length : 0;

    return {
      landmarks: mapped,
      confidence: Number(avgVis.toFixed(3)),
      detected: Object.keys(mapped).length > 0,
      points: Object.keys(mapped).length,
      silhouette_confidence: silhouetteConfidence,
    };
  } catch (e) {
    console.error("MediaPipe detect failed", e);
    return { landmarks: {}, confidence: 0, detected: false, error: String(e?.message || e) };
  }
}

// Pré-aquecer o modelo (opcional)
export async function warmupPose() { try { await getLandmarker(); } catch { /* ignore */ } }

// Compat com o stub anterior
export const poseDetector = {
  available: true,
  async load() { await getLandmarker(); return true; },
  async estimate(imageEl, view = "front", patientHints = null) {
    if (!imageEl?.src) return null;
    return detectPose(imageEl.src, view, patientHints);
  },
};
